import { useEffect, useState } from "react";
import { Briefcase, UserRound } from "lucide-react";
import { api } from "../../api/client";
import { TAIPEI } from "../attendance/time";
import { holidayName, isHoliday, useTwHolidays } from "../../lib/twHolidays";

// 門口打卡機上的月曆(業主 2026-09-30):
//   > 打卡頁面 目前公佈欄 佔最大位 我希望分隔一半 給日歷 最清楚的 包含誰休假/有什麼專案
//
// 業主選了**整個月的月曆格子**,專案**只寫名稱**(門口的螢幕訪客與客戶也看得到)。
// 休假只有已核准的、不寫假別 —— 這兩條後端就擋掉了(`routers/kiosk.py::calendar`),
// 這裡拿不到也就畫不出來。
//
// 樣子跟首頁日曆的「中」一樣(業主 2026-09-30:「想要跟首頁上的顯示方式一樣(中)
// 但是請假 專案 都在一格裡面用一行顯示」):專案是左邊一條色的那種條子、休假是帶人像的那種,
// **每一件固定一行、不折行**,放不下的字截掉。字比首頁的 11px 大 —— 門口是遠看的。
//
// 一格放得下四行(1080×1920 直式螢幕量過)。塞不下就放三行 + 「+N」:
// 四行全放、第五件默默消失的話,看的人會以為那天只有四件事。

interface Span {
  start_date: string;
  end_date: string;
}

interface KioskMonth {
  month: string;
  projects: (Span & { title: string })[];
  leaves: (Span & { name: string })[];
}

/** 月曆多久重抓一次。專案與假單不會一分鐘變好幾次,公告那一塊才要 5 秒。 */
const REFRESH_MS = 60_000;
/** 一格最多幾行。 */
const LINES = 4;
const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"];

/** 台北的今天,`YYYY-MM-DD`。 */
function todayInTaipei(now: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TAIPEI, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

function covers(span: Span, day: string): boolean {
  return span.start_date <= day && day <= span.end_date;
}

interface Line {
  key: string;
  text: string;
  kind: "project" | "leave";
}

function linesOf(data: KioskMonth, day: string): Line[] {
  const projects = data.projects
    .filter((p) => covers(p, day))
    .map((p, i) => ({ key: `p${i}`, text: p.title, kind: "project" as const }));
  const leaves = data.leaves
    .filter((l) => covers(l, day))
    .map((l, i) => ({ key: `l${i}`, text: `${l.name} 休假`, kind: "leave" as const }));
  return [...projects, ...leaves];
}

export function KioskCalendar() {
  const [data, setData] = useState<KioskMonth | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let alive = true;
    const load = () => {
      setNow(new Date());
      // 不帶月份 = 後端的「這個月」。跨月那一刻下一輪自己就換過去了,不必有人去點。
      void api
        .get<KioskMonth>("/kiosk/calendar")
        .then((got) => {
          if (alive) setData(got);
        })
        .catch(() => {});
    };
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  const today = todayInTaipei(now);
  const [year, month] = (data?.month ?? today.slice(0, 7)).split("-").map(Number);
  const first = new Date(Date.UTC(year, month - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lead = first.getUTCDay();
  const cells: (number | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = cells.length / 7;
  // 國定假日與六日的數字是紅字(業主 2026-10-15),資料與首頁日曆同一支
  const holidays = useTwHolidays([year]);

  return (
    <section
      aria-label="月曆"
      className="flex min-h-0 flex-col gap-1 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-3"
    >
      {/* 沒有月份標題、沒有圖例(業主 2026-09-30:「左上角 右上角都不需要 因為最上方已經顯示月份
          右上角多的 看細項就懂」)—— 專案的條子有公事包圖示、休假的寫著「休假」(「專案」兩個字 2026-10-15 拿掉)。高度全部留給格子。 */}
      <div className="grid grid-cols-7 text-center text-xs leading-none text-[var(--text-3)]">
        {WEEKDAYS.map((w) => (
          <div key={w}>{w}</div>
        ))}
      </div>
      <div
        className="grid min-h-0 flex-1 grid-cols-7 gap-1"
        style={{ gridTemplateRows: `repeat(${weeks}, minmax(0, 1fr))` }}
      >
        {cells.map((day, i) => {
          if (day === null) return <div key={`blank-${i}`} aria-hidden="true" />;
          const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isToday = iso === today;
          const off = isHoliday(holidays, iso);
          const offName = holidayName(holidays, iso);
          const all = data ? linesOf(data, iso) : [];
          const shown = all.length > LINES ? all.slice(0, LINES - 1) : all;
          const more = all.length - shown.length;
          return (
            <div
              key={iso}
              aria-label={`${month}/${day}`}
              aria-current={isToday ? "date" : undefined}
              className={
                "flex min-h-0 flex-col gap-0.5 overflow-hidden border p-1 " +
                (isToday ? "border-[var(--accent)] bg-[var(--surface)]" : "border-[var(--border)] bg-[var(--surface)]")
              }
            >
              {/* 今天是實心圓,與首頁日曆同一個樣子。 */}
              <span
                data-holiday={off ? "true" : undefined}
                title={offName || undefined}
                className={
                  "mb-0.5 self-start text-sm leading-none " +
                  (isToday
                    ? "inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-[var(--accent)] px-1 font-semibold text-[var(--on-accent)]"
                    : off
                      ? "text-[var(--danger)]"
                      : "text-[var(--text-2)]")
                }
              >
                {day}
              </span>
              <ul className="m-0 flex min-w-0 list-none flex-col gap-0.5 p-0">
                {shown.map((line) =>
                  line.kind === "project" ? (
                    <li
                      key={line.key}
                      className="flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap rounded-[var(--radius)] border-l-4 border-[var(--accent)] bg-[var(--accent-soft)] px-1 py-0.5 text-sm leading-tight text-[var(--accent-hover)]"
                    >
                      <Briefcase aria-hidden="true" size={13} className="shrink-0" />
                      <span className="truncate">{line.text}</span>
                    </li>
                  ) : (
                    <li
                      key={line.key}
                      className="flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-1 py-0.5 text-sm leading-tight text-[var(--text-2)]"
                    >
                      <UserRound aria-hidden="true" size={13} className="shrink-0" />
                      <span className="truncate">{line.text}</span>
                    </li>
                  ),
                )}
              </ul>
              {more > 0 ? <span className="text-sm leading-none text-[var(--text-3)]">+{more}</span> : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
